import { createContext, useContext, useEffect, useMemo, useReducer } from 'react'
import { STORAGE_KEYS } from '@/constants'

/** @typedef {{ items: string[] }} WishlistState */

const initialState = /** @type {WishlistState} */ ({ items: [] })

function reducer(state, action) {
  switch (action.type) {
    case 'HYDRATE':
      return { items: action.payload ?? [] }
    case 'TOGGLE': {
      const id = action.payload
      return state.items.includes(id)
        ? { items: state.items.filter((i) => i !== id) }
        : { items: [...state.items, id] }
    }
    case 'CLEAR':
      return { items: [] }
    default:
      return state
  }
}

const WishlistContext = createContext(null)

export function WishlistProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEYS.wishlist)
      if (raw) dispatch({ type: 'HYDRATE', payload: JSON.parse(raw) })
    } catch {
      /* ignore */
    }
  }, [])

  useEffect(() => {
    const id = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEYS.wishlist, JSON.stringify(state.items))
      } catch {
        /* ignore */
      }
    }, 300)
    return () => clearTimeout(id)
  }, [state.items])

  const value = useMemo(
    () => ({
      items: state.items,
      count: state.items.length,
      isInWishlist: (id) => state.items.includes(id),
      toggle: (id) => dispatch({ type: 'TOGGLE', payload: id }),
      clear: () => dispatch({ type: 'CLEAR' }),
    }),
    [state.items]
  )

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>
}

export function useWishlist() {
  const ctx = useContext(WishlistContext)
  if (!ctx) throw new Error('useWishlist must be used within <WishlistProvider>')
  return ctx
}
